import { baseApi, API_TAGS, createCacheTags, createEndpointBuilder, type Params, BaseQueryParams } from '@/shared/api';
import { createMultipartFormData, handleFileDownloadResponse } from '@/shared/lib';
import {
  ConsignmentsPageResponse,
  ConsignmentByIdRequest,
  ConsignmentBulkProcessRequest,
  ConsignmentBulkProcessResponse,
  BulkProcessParams,
  ConsignmentUpdateRequest,
  ConsignmentUpdateWithAttachmentsRequest,
  ConsignmentCreateRequest,
  ConsignmentUpdateParams,
  ConsignmentSummary,
  ConsignmentHistoryEntry,
  ConsignmentHistoryParams,
  ConsignmentDisposition,
  RawDisposition,
  RawDispositionResponse,
  ConsignmentAttachmentUploadParams,
  ConsignmentAttachmentUploadRequest,
  ConsignmentAttachmentUploadResponse,
  ConsignmentAttachmentDownloadParams,
  ConsignmentLineDetailParams,
  ConsignmentLineDetailResponse,
  ConsignmentsInfiniteQueryArgs,
  ConsignmentLinesParams,
  ConsignmentLinesResponse,
  ConsignmentLineOwnersParams,
  ConsignmentLineOwner,
  ConsignmentLineCreateParams,
  ConsignmentLineUpdateParams,
  ConsignmentLineWriteRequest
} from '../model/types';

const consignmentsEndpoint = createEndpointBuilder('/api/v2/consignments');

export const consignmentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getConsignments: builder.infiniteQuery<ConsignmentsPageResponse, ConsignmentsInfiniteQueryArgs, number>({
      infiniteQueryOptions: {
        initialPageParam: 0,
        getNextPageParam: (lastPage, _allPages, lastPageParam) => {
          const nextPage = lastPageParam + 1;
          if (nextPage >= lastPage.totalPages) {
            return undefined;
          }
          return nextPage;
        }
      },
      query: ({ queryArg, pageParam }) => ({
        url: consignmentsEndpoint(''),
        params: {
          ...queryArg,
          page: pageParam
        }
      }),
      providesTags: (_result, _error, { queryArg }) =>
        createCacheTags({
          baseTag: API_TAGS.CONSIGNMENTS,
          params: queryArg as unknown as Params
        })
    }),

    getConsignmentById: builder.query<ConsignmentSummary, ConsignmentByIdRequest>({
      query: ({ id, projectId, siteId }) => ({
        url: consignmentsEndpoint(`/${id}`),
        params: { projectId, siteId }
      }),
      providesTags: (_result, _error, { id }) => [API_TAGS.CONSIGNMENTS, { type: API_TAGS.CONSIGNMENTS, id }]
    }),

    bulkProcessConsignments: builder.mutation<
      ConsignmentBulkProcessResponse,
      ConsignmentBulkProcessRequest & BulkProcessParams
    >({
      query: ({ projectId, siteId, ...body }) => ({
        url: consignmentsEndpoint('/bulk-process'),
        method: 'PUT',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: [API_TAGS.CONSIGNMENTS]
    }),

    createConsignment: builder.mutation<ConsignmentSummary, ConsignmentCreateRequest & BaseQueryParams>({
      query: ({ projectId, siteId, ...body }) => ({
        url: consignmentsEndpoint(''),
        method: 'POST',
        params: { projectId, siteId },
        body: createMultipartFormData(body)
      }),
      invalidatesTags: [API_TAGS.CONSIGNMENTS]
    }),

    updateConsignment: builder.mutation<ConsignmentSummary, ConsignmentUpdateRequest & ConsignmentUpdateParams>({
      query: ({ id, projectId, siteId, ...body }) => ({
        url: consignmentsEndpoint(`/${id}`),
        method: 'PUT',
        params: { projectId, siteId },
        body
      }),
      invalidatesTags: (_result, _error, { id }) => [API_TAGS.CONSIGNMENTS, { type: API_TAGS.CONSIGNMENTS, id }]
    }),

    updateConsignmentWithAttachments: builder.mutation<
      ConsignmentSummary,
      ConsignmentUpdateWithAttachmentsRequest & ConsignmentUpdateParams
    >({
      query: ({ id, projectId, siteId, files, attachmentType = 'OT', comment, ...data }) => {
        const metadata: Record<string, string> = {
          attachmentType
        };
        if (comment) {
          metadata.comment = comment;
        }

        return {
          url: consignmentsEndpoint(`/${id}`),
          method: 'PUT',
          params: { projectId, siteId },
          body: createMultipartFormData(data, files, metadata)
        };
      },
      invalidatesTags: (_result, _error, { id }) => [API_TAGS.CONSIGNMENTS, { type: API_TAGS.CONSIGNMENTS, id }]
    }),

    getConsignmentHistory: builder.query<ConsignmentHistoryEntry[], ConsignmentHistoryParams>({
      query: ({ id, projectId, siteId, type }) => ({
        url: consignmentsEndpoint(`/${id}/history`),
        params: {
          projectId,
          siteId,
          ...(type && { type })
        }
      }),
      providesTags: (_result, _error, params) =>
        createCacheTags({
          baseTag: API_TAGS.CONSIGNMENT_HISTORY,
          params
        })
    }),

    getConsignmentDispositions: builder.query<ConsignmentDisposition[], ConsignmentByIdRequest>({
      query: ({ id, projectId, siteId }) => ({
        url: consignmentsEndpoint(`/${id}/dispositions`),
        params: { projectId, siteId }
      }),
      transformResponse: (response: RawDispositionResponse | RawDisposition[]) => {
        const mapDisposition = (line: RawDisposition): ConsignmentDisposition => ({
          itemCode: line.itemCode || line.productCode || '-',
          description: line.description || line.productDescription || '-',
          unit: line.unit || line.uom || '-',
          totalPerSrf: typeof line.totalPerSrf === 'number' ? line.totalPerSrf : 0,
          remainsWithSender: line.remainsWithSender || 0,
          cancelledBySender: line.cancelledBySender || 0,
          receivedFromSender: line.receivedFromSender || 0,
          transferOut: line.transferOut || 0,
          transferIn: line.transferIn || 0,
          stockOnHand: line.stockOnHand || 0,
          reportedLost: line.reportedLost || 0,
          releasedToReceiver: line.releasedToReceiver || 0,
          o: line.o,
          locations: (line.locations || []).map((loc) => ({
            locationName: loc.locationName || '-',
            received: loc.received || 0,
            transferOut: loc.transferOut || 0,
            transferIn: loc.transferIn || 0,
            stockOnHand: loc.stockOnHand || 0,
            reportedLost: loc.reportedLost || 0,
            released: loc.released || 0,
            movements: (loc.movements || []).map((mov) => ({
              date: mov.date || '-',
              description: mov.description || null,
              quantity: mov.quantity || 0,
              stockOnHand: mov.stockOnHand || 0
            }))
          }))
        });

        if (response && !Array.isArray(response)) {
          if (Array.isArray(response.lines)) return response.lines.map(mapDisposition);
          if (Array.isArray(response.results)) return response.results.map(mapDisposition);
        }
        if (Array.isArray(response)) {
          return response.map(mapDisposition);
        }
        return [];
      },
      providesTags: (_result, _error, { id }) => [API_TAGS.CONSIGNMENTS, { type: API_TAGS.CONSIGNMENTS, id }]
    }),

    uploadConsignmentAttachment: builder.mutation<
      ConsignmentAttachmentUploadResponse,
      ConsignmentAttachmentUploadParams & ConsignmentAttachmentUploadRequest
    >({
      query: ({ id, projectId, siteId, file, attachmentType, comment }) => {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('attachmentType', attachmentType);
        if (comment) {
          formData.append('comment', comment);
        }

        return {
          url: consignmentsEndpoint(`/${id}/attachments`),
          method: 'POST',
          params: { projectId, siteId },
          body: formData
        };
      },
      invalidatesTags: (_result, _error, { id }) => [API_TAGS.CONSIGNMENTS, { type: API_TAGS.CONSIGNMENTS, id }]
    }),

    downloadConsignmentAttachment: builder.query<Blob, ConsignmentAttachmentDownloadParams>({
      query: ({ id, attachmentId, projectId, siteId }) => ({
        url: consignmentsEndpoint(`/${id}/attachment/${attachmentId}`),
        params: { projectId, siteId },
        responseHandler: (response) => handleFileDownloadResponse(response, 'attachment.pdf'),
        cache: 'no-cache'
      })
    }),

    getConsignmentLineDetail: builder.query<ConsignmentLineDetailResponse, ConsignmentLineDetailParams>({
      query: ({ lineId, projectId, siteId }) => ({
        url: consignmentsEndpoint(`/lines/${lineId}`),
        params: { projectId, siteId }
      }),
      providesTags: (_result, _error, { lineId }) =>
        createCacheTags({
          baseTag: API_TAGS.CONSIGNMENTS,
          params: { type: 'line', id: lineId },
          includeBaseTag: false
        })
    }),

    getConsignmentLines: builder.query<ConsignmentLinesResponse, ConsignmentLinesParams>({
      query: ({ consignmentId, projectId, siteId }) => ({
        url: consignmentsEndpoint(`/${consignmentId}/lines`),
        params: { projectId, siteId }
      }),
      providesTags: (_result, _error, { consignmentId, projectId, siteId }) =>
        createCacheTags({
          baseTag: API_TAGS.CONSIGNMENTS,
          params: { projectId, siteId, consignmentId },
          includeBaseTag: false
        })
    }),

    getConsignmentLineOwners: builder.query<ConsignmentLineOwner[], ConsignmentLineOwnersParams>({
      query: ({ projectId, siteId, senderOrgId }) => ({
        url: consignmentsEndpoint('/lines/owners'),
        params: { projectId, siteId, senderOrgId }
      }),
      providesTags: (_result, _error, params) =>
        createCacheTags({
          baseTag: API_TAGS.CONSIGNMENTS,
          params: { ...params, type: 'line-owners' }
        })
    }),

    downloadConsignmentReport: builder.mutation<Blob, { id: string; lang?: string; opt?: number }>({
      query: ({ id, lang = 'en', opt = 1 }) => ({
        url: `/api/v1/reports/consignment-report/${id}`,
        params: { lang, opt },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache'
      })
    }),

    downloadShippingLabels: builder.mutation<Blob, { id: string; lang?: string; opt?: number; o?: number }>({
      query: ({ id, lang = 'en', opt = 1, o }) => ({
        url: `/api/v1/reports/consignment-shipping-label/${id}`,
        params: { lang, opt, o },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache'
      })
    }),
    downloadConsignmentTag: builder.mutation<Blob, { id: string; lang?: string; opt?: number; ext?: number }>({
      query: ({ id, lang = 'en', opt = 1, ext }) => ({
        url: `/api/v1/reports/consignment-tag/${id}`,
        params: { lang, opt, ext },
        responseHandler: (response) => response.blob(),
        cache: 'no-cache'
      })
    }),

    createConsignmentLine: builder.mutation<
      ConsignmentLineDetailResponse,
      { params: ConsignmentLineCreateParams; data: ConsignmentLineWriteRequest }
    >({
      query: ({ params, data }) => ({
        url: consignmentsEndpoint('/lines'),
        method: 'POST',
        params,
        body: data
      }),
      invalidatesTags: (_result, _error, { params: { projectId, siteId }, data }) =>
        createCacheTags({
          baseTag: API_TAGS.CONSIGNMENTS,
          params: { projectId, siteId, consignmentId: data.consignmentId },
          includeBaseTag: false
        })
    }),

    updateConsignmentLine: builder.mutation<
      ConsignmentLineDetailResponse,
      { params: ConsignmentLineUpdateParams; data: ConsignmentLineWriteRequest }
    >({
      query: ({ params: { id, siteId, projectId }, data }) => ({
        url: consignmentsEndpoint(`/lines/${id}`),
        method: 'PUT',
        params: { projectId, siteId },
        body: data
      }),
      invalidatesTags: (_result, _error, { params: { id, projectId, siteId }, data }) => [
        ...createCacheTags({
          baseTag: API_TAGS.CONSIGNMENTS,
          params: { type: 'line', id },
          includeBaseTag: false
        }),
        ...createCacheTags({
          baseTag: API_TAGS.CONSIGNMENTS,
          params: {
            projectId,
            siteId,
            consignmentId: data.consignmentId
          },
          includeBaseTag: false
        })
      ]
    }),

    deleteConsignmentLine: builder.mutation<void, ConsignmentLineUpdateParams>({
      query: ({ id, projectId, siteId }) => ({
        url: consignmentsEndpoint(`/lines/${id}`),
        method: 'DELETE',
        params: { projectId, siteId }
      }),
      invalidatesTags: (_result, _error, { projectId, siteId, consignmentId }) => {
        if (consignmentId) {
          return createCacheTags({
            baseTag: API_TAGS.CONSIGNMENTS,
            params: { projectId, siteId, consignmentId },
            includeBaseTag: false
          });
        }
        return [];
      }
    })
  })
});

export const {
  useGetConsignmentByIdQuery,
  useGetConsignmentsInfiniteQuery,
  useBulkProcessConsignmentsMutation,
  useCreateConsignmentMutation,
  useUpdateConsignmentMutation,
  useUpdateConsignmentWithAttachmentsMutation,
  useGetConsignmentHistoryQuery,
  useGetConsignmentDispositionsQuery,
  useUploadConsignmentAttachmentMutation,
  useLazyDownloadConsignmentAttachmentQuery,
  useGetConsignmentLineDetailQuery,
  useGetConsignmentLinesQuery,
  useGetConsignmentLineOwnersQuery,
  useDownloadConsignmentReportMutation,
  useDownloadShippingLabelsMutation,
  useDownloadConsignmentTagMutation,
  useCreateConsignmentLineMutation,
  useUpdateConsignmentLineMutation,
  useDeleteConsignmentLineMutation
} = consignmentsApi;
